// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { createFileRoute, Navigate, useBlocker } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useTaskQuery } from "@/api/hooks/use-tasks";
import { AccessDenied } from "@/components/shared/access-denied";
import { UnsavedChangesDialog } from "@/components/shared/unsaved-changes-dialog";
import { TaskEditTab, type TaskEditTabHandle } from "@/features/tasks/task-edit-tab";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";

export const Route = createFileRoute("/_authenticated/tasks/$subtype/edit")({
  component: TaskEditPage,
});

/* The card editor carries its own section navigation, so the page needs no tab row of its own. */
function TaskEditPage() {
  const { subtype } = Route.useParams();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(PAGE_PERMISSIONS.tasksEdit);
  const { data: task } = useTaskQuery(subtype);

  const [isFormDirty, setIsFormDirty] = useState(false);
  const editTabRef = useRef<TaskEditTabHandle>(null);

  const blocker = useBlocker({
    shouldBlockFn: () => isFormDirty,
    enableBeforeUnload: () => isFormDirty,
    withResolver: true,
  });

  if (!canEdit) return <AccessDenied permission={PAGE_PERMISSIONS.tasksEdit} />;
  // A built-in type has no definition to configure — send a stale link back to the read page.
  if (task?.kind === "builtIn") return <Navigate to="/tasks/$subtype" params={{ subtype }} search={{}} replace />;

  return (
    <div>
      <TaskEditTab ref={editTabRef} subtype={subtype} canEdit={canEdit} onDirtyChange={setIsFormDirty} />

      <UnsavedChangesDialog
        open={blocker.status === "blocked"}
        onStay={() => blocker.reset?.()}
        onDiscard={() => {
          editTabRef.current?.reset();
          blocker.proceed?.();
        }}
      />
    </div>
  );
}
